/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import cloneDeep from 'lodash.clonedeep';
import isundefined from 'lodash.isundefined';
// eslint-disable-next-line no-restricted-imports
import {Page} from '@ion/applications/src/types/pages';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Entity, GenericObject, User} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {
  getPersistedRoute,
  getRoute,
  getRouteByFeature,
  isRoute,
  isValidPath,
  Route
} from '@/routes/RouteDefinitions';
import {isEnabled as isPegWizardEnabled} from '@/routes/toolbox/pegwizard/store';
import Features, {
  ADMIN,
  ADMIN_ACCOUNTS,
  ADMIN_AEMP_CONFIG,
  ADMIN_APPLICATIONS,
  ADMIN_ASSETS,
  ADMIN_DEVICE_CONFIGS,
  ADMIN_GROUPS,
  ADMIN_PEG_BEHAVIORS,
  ADMIN_PEG_LABELS,
  ADMIN_ROLES,
  ADMIN_USERS,
  ALERTS_NOTIFICATIONS,
  ALERTS_SETUP,
  DASHBOARD_PAGE,
  DEVICES,
  MANUFACTURED_DEVICES,
  MANUFACTURING,
  MANUFACTURING_APPLICATION_IDS,
  MANUFACTURING_INVENTORY,
  MANUFACTURING_PART_NUMBERS,
  MANUFACTURING_SHIPMENTS,
  OEM_VALIDATION_RULES,
  PROVISIONING,
  PROVISIONING_DEVICE_FILES,
  PROVISIONING_DOWNLOAD_JOBS,
  PROVISIONING_JOBS_SEARCH_ENGINE,
  REPORTS,
  REPORTS_AVAILABLE_REPORTS,
  REPORTS_SCHEDULED_REPORTS,
  SENSOR_INSTALLER,
  SENSORS,
  SERVICES,
  SERVICES_CARB,
  SYSTEM_CONFIG,
  SYSTEM_CONFIG_ALERT_TYPES,
  SYSTEM_CONFIG_CARRIER_PART_NUMBERS,
  SYSTEM_CONFIG_DEVICE_TYPES,
  TOOLBOX,
  TOOLBOX_KEYFILE_BUILDER,
  TOOLBOX_MODBUS,
  TOOLBOX_MODBUS_EDITOR,
  TOOLBOX_PEG_WIZARD,
  VEHICLE,
  VEHICLE_CAN_REQUESTS,
  VEHICLE_MANAGER,
  VEHICLE_PROFILE
} from '@/security/Features';
import {hasInternalVehicleEngineeringRole} from '@/security/ModuleAccessControl';
import {
  APPLICATION_INITIALIZATION_COMPLETE
} from '@/store/actions/initialization';
import {
  PAGE_SECURITY_INITIALIZED,
  SET_ACTIVE_INDEX,
  SET_NAV_COLLAPSED_STATUS,
  SET_PAGE,
  TOGGLE_SECONDARY_NAV
} from '@/store/actions/nav';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export {authorizedPages} from '../../utils/nav-utils';

/**
 * Defines a mapping of iOn Feature ids to their corresponding translation
 * entry keys.
 */
const pageFeatureIdMap = {
  [DASHBOARD_PAGE]                     : 'dashboard',
  [PROVISIONING]                       : 'provisioning',
  [PROVISIONING_DEVICE_FILES]          : 'devicefiles',
  [PROVISIONING_DOWNLOAD_JOBS]         : 'downloadjobs',
  [PROVISIONING_JOBS_SEARCH_ENGINE]    : 'jobsearchengine',
  [ADMIN]                              : 'admin',
  [ADMIN_APPLICATIONS]                 : 'adminApplications',
  [ADMIN_ACCOUNTS]                     : 'adminAccounts',
  [ADMIN_AEMP_CONFIG]                  : 'adminAempconfig',
  [ADMIN_ASSETS]                       : 'adminAssets',
  [ADMIN_USERS]                        : 'users',
  [ADMIN_PEG_BEHAVIORS]                : 'adminPegBehaviors',
  [ADMIN_DEVICE_CONFIGS]               : 'adminDeviceConfigs',
  [ALERTS_NOTIFICATIONS]               : 'notifications',
  [ADMIN_ROLES]                        : 'roles',
  [ALERTS_SETUP]                       : 'adminNotifications',
  [ADMIN_GROUPS]                       : 'adminGroups',
  [SYSTEM_CONFIG]                      : 'systemConfig',
  [SYSTEM_CONFIG_ALERT_TYPES]          : 'systemconfigNotificationType',
  [SYSTEM_CONFIG_DEVICE_TYPES]         : 'deviceTypes',
  [SYSTEM_CONFIG_CARRIER_PART_NUMBERS] : 'carrierPartNumbers',
  [REPORTS]                            : 'reports',
  [DEVICES]                            : 'devices',
  [TOOLBOX]                            : 'toolbox',
  [TOOLBOX_KEYFILE_BUILDER]            : 'keyfileBuilder',
  [TOOLBOX_MODBUS_EDITOR]              : 'modbusEditor',
  [TOOLBOX_MODBUS]                     : 'modbus',
  [MANUFACTURING]                      : 'manufacturing',
  [MANUFACTURED_DEVICES]               : 'manufacturedDevices',
  [MANUFACTURING_INVENTORY]            : 'manufacturingInventory',
  [MANUFACTURING_APPLICATION_IDS]      : 'manufacturingApplicationIds',
  [MANUFACTURING_PART_NUMBERS]         : 'manufacturingPartNumbers',
  [MANUFACTURING_SHIPMENTS]            : 'manufacturingShipments',
  [VEHICLE]                            : 'vehicle',
  [VEHICLE_MANAGER]                    : 'vehicleManager',
  [VEHICLE_PROFILE]                    : 'vehicleProfile',
  [VEHICLE_CAN_REQUESTS]               : 'vehicleCanRequests',
  [OEM_VALIDATION_RULES]               : 'validation',
  [SENSORS]                            : 'sensors',
  [SENSOR_INSTALLER]                   : 'sensorInstaller',
  [SERVICES]                           : 'services',
  [SERVICES_CARB]                      : 'carb'
};

/**
 * @private
 *
 * Provides a utility which updates all authorized page names and labels with
 * the corresponding translation for the page's feature id.
 */
const translateAuthorizedPages = (pages: Page[]): Page[] => {
  return pages?.map((page: Page) => {
    page.name = page.label = translate(`nav:side.${pageFeatureIdMap[page?.id]}`);
    return page;
  });
};

/**
 * Provides a utility which returns the navigation item index for the given
 * route.
 */
const getIndex = (pages: Page[], page: string): number => {
  let index: number;
  pages?.find(({pages: subpages, route}: Page, i) => {
    const match    = getRoute(route) === getRoute(page as Route);
    const hasChild = subpages && !isundefined(getIndex(subpages as Page[], page));
    if (match || hasChild) {
      index = i;
      return true;
    }
  });
  return index;
};

/**
 * @private
 */
const getFallbackPageId = (pages: Page[]): string => {
  return DASHBOARD_PAGE;
};

/**
 * @private
 */
const getDefaultPage = (route: any, fromLogin: boolean, pages: Page[]): Route => {
  const defaultPageId = UserPreferences.get('defaultPageId') || DASHBOARD_PAGE;
  let pageId = Features.hasFeature(defaultPageId) ? defaultPageId : getFallbackPageId(pages);
  if (defaultPageId === PROVISIONING_JOBS_SEARCH_ENGINE && Features.hasFeature(PROVISIONING_DOWNLOAD_JOBS)) {
    pageId = defaultPageId;
  }
  const defaultPage   = getRouteByFeature(pageId);
  const page = Array.isArray(defaultPage) ? defaultPage[0] : defaultPage;
  if (!fromLogin) {
    const current = isValidPath(route) ? route?.pathname : null;
    return (current || getPersistedRoute() || page) as Route;
  }
  return page;
};

/**
 * Provide an utility method to include the jobs2.0 Feature to show the
 * Jobs2.0 options in the Menu, when the logged in user has
 * Jobs Feature and Permissions tagged in the Role.
 */
const setJobsAvailablePages = (authorized: GenericObject) => {
  const provisioning = authorized.find(({id}: Page) => id === PROVISIONING);
  if (provisioning) {
    const {pages} = PROVISIONING_MODULE || {};
    pages?.push({...JOBS_SEARCH_ENGINE});
    return authorized?.map((item) => {
      if (item?.id === PROVISIONING) {
        item.isAuthorized = true;
        item.pages = pages;
      }
      return item;
    });
  }
};

/**
 * setAvailablePages
 */
const setAvailablePages = (draft: NavState, {route, user}) => {
  let authorized     = Features.getPermittedPages({pages: pages()}) as Page[];
  const hasDashboard = authorized.find(({id}: Page) => id === DASHBOARD.id);
  const secondary    = authorized.find((page: Page) => page.id === ADMIN);
  if (!hasDashboard) {
    authorized = [DASHBOARD, ...authorized] as Page[];
  }
  setJobsAvailablePages(authorized);
  authorized  = setFeatureFlagEnabledPages(authorized, user);
  draft.pages = translateAuthorizedPages(authorized) as Page[];
  draft.defaultPage   = getDefaultPage(route, isRoute(Route.LOGIN), draft.pages);
  draft.selectedIndex = getIndex(draft.pages, draft.defaultPage);
  if (secondary) {
    draft.secondaryNav.pages = secondary.pages as Page[];
  }
};

/**
 * Provides a utility which augments the authorized pages to which the user has
 * access for pages which are to be provided based on specific feature flags.
 *
 * @param authorized The list of pages which have been authorized based on the
 *        user's Role Features.
 */
const setFeatureFlagEnabledPages = (authorized: Page[], user: User): Page[] => {
  if (hasInternalVehicleEngineeringRole(user)) {
    authorized = setCanRequestsPage(authorized);
  }

  if (isPegWizardEnabled()) {
    return setPegWizardPage(authorized);
  }
  return authorized;
};

const setCanRequestsPage = (authorized: Page[]): Page[] => {
  const vehicle = authorized.find(({id}: Page) => id === VEHICLE);
  const canRequestsPage: Page = {
    id            : VEHICLE_CAN_REQUESTS,
    route         : Route.VEHICLE_CAN_REQUESTS,
    isActive      : true,
    isAuthorized  : true,
    showSideSheet : false,
    isPreview     : true
  };
  if (vehicle) {
    if (!vehicle.pages?.some(({id}: Page) => id === VEHICLE_CAN_REQUESTS)) {
      vehicle.pages = [...(vehicle.pages || []), canRequestsPage];
    }
    return authorized;
  }
  return [
    ...authorized,
    {
      id            : VEHICLE,
      route         : Route.VEHICLE,
      isActive      : true,
      isAuthorized  : true,
      showSideSheet : false,
      isPreview     : true,
      pages         : [canRequestsPage]
    }
  ];
};

/**
 * @private
 *
 * Adds the pegwizard Page the Toolbox page in the list of authorized pages. If the
 * Toolbox page has not been added, it will be added with a single sub-page, the
 * PEG Wizard Page.
 */
const setPegWizardPage = (authorized: Page[]): Page[] => {
  const pegWizard = {
    id           : TOOLBOX_PEG_WIZARD,
    route        : Route.TOOLBOX_PEG_WIZARD,
    isActive     : true,
    isAuthorized : true,
    isPreview    : true,
    isNew        : true
  };
  const toolbox = authorized.find(({id}: Page) => id === TOOLBOX);
  if (!toolbox) {
    authorized.push({
      ...TOOLBOX_MODULE,
      isAuthorized : true,
      pages        : [pegWizard]
    } as unknown as any);
  } else {
    (toolbox.pages ||= []).push(pegWizard);
  }
  return authorized;
};

// defines the shape of this reducers state ...
export type NavState = {
  pages: Page[];
  defaultPage: Route;
  currentPage: string;
  previousPage: string;
  selectedIndex: number;
  activeIndex: number;
  collapsed: boolean;
  isInternalRoute: boolean;
  isUserProfileView: boolean;
  pageSecurityInitialized: boolean;
  secondaryNav: {
    pages: Page[];
    show: boolean;
  };
  sideSheet: {
    activeSideSheet: null | string;
    desktopSideSheetOpen: boolean;
    mobileSideSheetOpen: boolean;
  };
};

// defines the initial state of this reducer ...
export const initialState: NavState = {
  pages                   : [],
  defaultPage             : Route.DASHBOARD,
  currentPage             : null,
  previousPage            : null,
  selectedIndex           : NaN,
  activeIndex             : -1,
  collapsed               : false,
  isInternalRoute         : false,
  isUserProfileView       : false,
  pageSecurityInitialized : false,
  secondaryNav            : {
    pages : [],
    show  : false
  },
  sideSheet : {
    activeSideSheet      : null,
    desktopSideSheetOpen : false,
    mobileSideSheetOpen  : false
  }
};

const DASHBOARD = {
  id           : DASHBOARD_PAGE,
  route        : Route.DASHBOARD,
  isActive     : true,
  isAuthorized : true,
  isPreview    : true
};

const JOBS_SEARCH_ENGINE = {
  id           : PROVISIONING_JOBS_SEARCH_ENGINE,
  route        : Route.PROVISIONING_JOBS_SEARCH_ENGINE,
  isActive     : true,
  isAuthorized : true,
  isPreview    : true
};

const ADMIN_MODULE = {
  id            : ADMIN,
  route         : Route.ADMIN,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [{
    id            : ADMIN_ACCOUNTS,
    route         : Route.ADMIN_ACCOUNTS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_AEMP_CONFIG,
    route         : Route.ADMIN_AEMP_CONFIG,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_APPLICATIONS,
    route         : Route.ADMIN_APPLICATIONS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_ASSETS,
    route         : Route.ADMIN_ASSETS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_DEVICE_CONFIGS,
    route         : Route.ADMIN_DEVICE_CONFIGS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_GROUPS,
    route         : Route.ADMIN_GROUPS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ALERTS_SETUP,
    route         : Route.ADMIN_NOTIFICATIONS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_PEG_BEHAVIORS,
    route         : Route.ADMIN_PEG_BEHAVIORS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_PEG_LABELS,
    route         : Route.ADMIN_PEG_LABELS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_ROLES,
    route         : Route.ADMIN_ROLES,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  },
  {
    id            : ADMIN_USERS,
    route         : Route.ADMIN_USERS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  }]
};

const PROVISIONING_MODULE = {
  id            : PROVISIONING,
  route         : Route.PROVISIONING,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [
    {
      id            : PROVISIONING_DEVICE_FILES,
      route         : Route.PROVISIONING_DEVICE_FILES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }
  ]
};

const NOTIFICATION_MODULE = {
  id           : ALERTS_NOTIFICATIONS,
  route        : Route.NOTIFICATIONS,
  isActive     : true,
  isAuthorized : true,
  isPreview    : true
};

const SYSTEM_CONFIG_MODULE = {
  id            : SYSTEM_CONFIG,
  route         : Route.SYSTEM_CONFIG,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [{
    id            : SYSTEM_CONFIG_DEVICE_TYPES,
    route         : Route.SYSTEM_CONFIG_DEVICE_TYPES,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  }, {
    id            : SYSTEM_CONFIG_ALERT_TYPES,
    route         : Route.SYSTEM_CONFIG_ALERT_TYPES,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  }, {
    id            : SYSTEM_CONFIG_CARRIER_PART_NUMBERS,
    route         : Route.SYSTEM_CONFIG_CARRIER_PART_NUMBERS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  }]
};

const REPORT_MANAGER_PAGE: Page = {
  id            : REPORTS_AVAILABLE_REPORTS,
  route         : Route.ANALYTICS_SCHEDULER,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true
};

const REPORT_MODULE: Page =  {
  id            : REPORTS,
  route         : Route.REPORTS,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  secondaryNav  : true,
  pages         : [{
    id            : REPORTS_AVAILABLE_REPORTS,
    route         : Route.ANALYTICS_DASHBOARDS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  }]
};

const DEVICES_MODULE =  {
  id            : DEVICES,
  route         : Route.DEVICES,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true
};

const TOOLBOX_MODULE = {
  id            : TOOLBOX,
  route         : Route.TOOLBOX,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [
    {
      id            : TOOLBOX_KEYFILE_BUILDER,
      route         : Route.TOOLBOX_KEYFILE_BUILDER,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : TOOLBOX_MODBUS,
      route         : Route.TOOLBOX_MODBUS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }
  ]
};

const MANUFACTURING_MODULE = {
  id            : MANUFACTURING,
  route         : Route.MANUFACTURING,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [
    {
      id            : MANUFACTURING_INVENTORY,
      route         : Route.MANUFACTURING_INVENTORY,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : MANUFACTURED_DEVICES,
      route         : Route.MANUFACTURED_DEVICES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : MANUFACTURING_APPLICATION_IDS,
      route         : Route.MANUFACTURING_APPLICATION_IDS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : MANUFACTURING_PART_NUMBERS,
      route         : Route.MANUFACTURING_PART_NUMBERS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : MANUFACTURING_SHIPMENTS,
      route         : Route.MANUFACTURING_SHIPMENTS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }
  ]
};

const VEHICLE_ENGINEERING_SERVICES_MODULE = {
  id            : VEHICLE,
  route         : Route.VEHICLE,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [
    {
      id            : VEHICLE_MANAGER,
      route         : Route.VEHICLE_MANAGER,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : VEHICLE_PROFILE,
      route         : Route.VEHICLE_PROFILE,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : VEHICLE_CAN_REQUESTS,
      route         : Route.VEHICLE_CAN_REQUESTS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    },
    {
      id            : OEM_VALIDATION_RULES,
      route         : Route.OEM_VALIDATION_RULES,
      isActive      : true,
      isAuthorized  : true,
      showSideSheet : false,
      isPreview     : true
    }
  ]
};

const SENSORS_MODULE = {
  id            : SENSORS,
  route         : Route.SENSORS,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [
    {
      id            : SENSOR_INSTALLER,
      route         : Route.SENSOR_INSTALLER,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }]
};

const SERVICES_MODULE = {
  id            : SERVICES,
  route         : Route.SERVICES,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  pages         : [
    {
      id            : SERVICES_CARB,
      route         : Route.SERVICES_CARB,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }]
};

/**
 * Defines all CTC Admin application pages definitions. Upon post initialization
 * (after the user has authenticated), the pages are passed through the Features
 * API which filters them based on those to which the User is authorized to view
 * based on their given Role.
 *
 * In addition, when initially referenced, the isPreview value fro specific pages
 * will be set based on the value of the "featureFlagsEnabled" entry from the
 * applications environment.json document. This allow for externally enabling
 * and disabling of flagged features.
 */
export const pages = (): Page[] => {
  const defaultPages = [
    DASHBOARD,
    DEVICES_MODULE,
    PROVISIONING_MODULE,
    getReportsModule(),
    NOTIFICATION_MODULE,
    ADMIN_MODULE,
    TOOLBOX_MODULE,
    SYSTEM_CONFIG_MODULE,
    MANUFACTURING_MODULE,
    VEHICLE_ENGINEERING_SERVICES_MODULE,
    SENSORS_MODULE,
    SERVICES_MODULE
  ];
  return defaultPages.filter(page => page);
};

/**
 * Based on user roles & permissions returns the accessible Reports pages.
 */
const getReportsModule = (): Page => {
  const reportModule = cloneDeep(REPORT_MODULE);
  if (
    Features.hasPermission(Entity.reports, Operation.Create) ||
    Features.hasPermission(Entity.reports, Operation.Update)
  ) {
    reportModule.pages.push(REPORT_MANAGER_PAGE);
  }
  return Features.hasPermission(Entity.reports, Operation.Read) ? reportModule : null;
};

// defines the reducers ...
const reducers = {
  [SET_PAGE] : (draft: NavState, {page}) => {
    draft.selectedIndex   = getIndex(draft.pages, getRoute(page));
    draft.previousPage    = draft.currentPage;
    draft.currentPage     = getRoute(page);
    draft.isInternalRoute = true;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : setAvailablePages,
  [PAGE_SECURITY_INITIALIZED]           : (draft: NavState) => {
    draft.pageSecurityInitialized = true;
  },
  [TOGGLE_SECONDARY_NAV] : (draft: NavState, {show, index}) => {
    draft.secondaryNav.show = show;
    draft.selectedIndex = index;
  },
  [SET_ACTIVE_INDEX] : (draft, {index}) => {
    draft.activeIndex = index;
  },
  [SET_NAV_COLLAPSED_STATUS] : (draft: NavState, {collapsed}) => {
    draft.collapsed = collapsed;
  }
};

// defines all reducers for actions of interest to this reducer ...
export default produce((draft: NavState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
